// FLEET LIVE's one frame loop, wired to the browser (#6778): `createFrameScheduler`
// (`fleet-live-frame.ts`) fed by `requestAnimationFrame`, the tab's `visibilitychange`, an
// `IntersectionObserver` on the section with a 120 px margin, and the
// reduced-motion query (`./reduced-motion.ts`). Every canvas registers its painter here, so the section
// holds ONE subscription to each of those and one frame chain, however many panels draw.
//
// A browser without `IntersectionObserver` (and the test DOM) counts the section as on
// screen; one without `requestAnimationFrame` draws nothing, which is the still frame's
// worst case rather than a throw.
//
// THE FLOATING CORE (#6779) shows exactly when the Core panel has scrolled away, which is
// often when the whole section is off screen, so while it is shown it keeps the loop awake
// (`keepAwake`, the prototype's `onScreen || pip.shown()`, `:4348`). Each frame says whether
// the SECTION is on screen (`onScreen`), and the section's own canvases skip their paint
// while it is not; a return on screen marks the frame dirty, so a still frame repaints.

import { createSignal, onCleanup } from 'solid-js';
import { createFrameScheduler } from './fleet-live-frame';
import { createMotionTracker, type Motion, type MotionTracker } from './fleet-live-motion';
import { type LivePalette, paletteOf } from './fleet-live-palette';
import { reducedMotionQuery } from './reduced-motion';

/** The section counts as on screen until it is more than this far outside the viewport. */
export const ON_SCREEN_MARGIN_PX = 120;

/** What one painter is handed each frame: `motion` is `null` on a still frame. */
export type LiveFrame = {
  readonly now: number;
  readonly dt: number;
  readonly motion: Motion | null;
  /** The section itself is on screen (the floating Core may keep the loop awake without it). */
  readonly onScreen: boolean;
};

export type LiveLoop = {
  readonly register: (paint: (f: LiveFrame) => void) => () => void;
  /** The data, the size or the theme changed: paint again. */
  readonly invalidate: () => void;
  readonly reducedMotion: () => boolean;
  readonly motion: MotionTracker;
  /** The canvas colours, re-read when the theme changes. `null` before `observe`. */
  readonly palette: () => LivePalette | null;
  /** Observe the section for the on-screen gate and its theme. */
  readonly observe: (el: Element) => void;
  /** A painter outside the section (the floating Core) is shown: keep the loop running. */
  readonly keepAwake: (on: boolean) => void;
};

export function createLiveLoop(): LiveLoop {
  const mq = reducedMotionQuery();
  const [reduced, setReduced] = createSignal(mq?.matches ?? false);
  let onScreen = true;
  let awake = false;
  const painters = new Set<(f: LiveFrame) => void>();
  const motion = createMotionTracker();
  const raf = globalThis.requestAnimationFrame?.bind(globalThis);
  const caf = globalThis.cancelAnimationFrame?.bind(globalThis);

  const scheduler = createFrameScheduler({
    requestFrame: (cb) => (raf ? raf(cb) : 0),
    cancelFrame: (id) => caf?.(id),
    reducedMotion: reduced,
    tabVisible: () => globalThis.document?.visibilityState !== 'hidden',
    onScreen: () => onScreen || awake,
    draw: (now, dt) => {
      const frame: LiveFrame = { now, dt, motion: reduced() ? null : motion.at(now), onScreen };
      for (const paint of painters) paint(frame);
    },
  });

  const onVisibility = (): void => scheduler.wake();
  globalThis.document?.addEventListener('visibilitychange', onVisibility);
  const onReduce = (e: MediaQueryListEvent): void => {
    setReduced(e.matches);
    scheduler.invalidate();
  };
  mq?.addEventListener?.('change', onReduce);
  let io: IntersectionObserver | undefined;
  let theme: MutationObserver | undefined;
  let section: Element | undefined;
  let palette: LivePalette | null = null;
  // READ ONLY OFF A CONNECTED SECTION. The section's `ref` runs before Solid inserts it,
  // and a detached element's computed style has no custom properties: a palette read
  // there is every token '', and `addColorStop('')` throws, ending the frame for every
  // painter registered after the one that threw. Until the section is in the document the
  // palette is `null`, which every canvas already treats as "paint nothing yet".
  const read = (): LivePalette | null => {
    if (palette === null && section?.isConnected === true) palette = paletteOf(section);
    return palette;
  };
  const reread = (): void => {
    palette = null;
    read();
    scheduler.invalidate();
  };
  const scheme =
    typeof globalThis.matchMedia === 'function'
      ? globalThis.matchMedia('(prefers-color-scheme: light)')
      : null;
  scheme?.addEventListener?.('change', reread);

  onCleanup(() => {
    scheduler.stop();
    globalThis.document?.removeEventListener('visibilitychange', onVisibility);
    mq?.removeEventListener?.('change', onReduce);
    io?.disconnect();
    theme?.disconnect();
    scheme?.removeEventListener?.('change', reread);
  });

  return {
    register(paint) {
      painters.add(paint);
      scheduler.invalidate();
      return () => {
        painters.delete(paint);
      };
    },
    invalidate: () => scheduler.invalidate(),
    reducedMotion: reduced,
    motion,
    palette: read,
    keepAwake(on) {
      if (awake === on) return;
      awake = on;
      if (on) scheduler.invalidate();
      else scheduler.wake();
    },
    observe(el) {
      section = el;
      palette = null;
      read();
      if (typeof globalThis.MutationObserver === 'function') {
        theme?.disconnect();
        theme = new MutationObserver(reread);
        theme.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['data-theme'],
        });
      }
      if (typeof globalThis.IntersectionObserver !== 'function') return;
      io?.disconnect();
      io = new IntersectionObserver(
        (entries) => {
          const was = onScreen;
          onScreen = entries.some((e) => e.isIntersecting);
          // Back on screen: the section's canvases skipped any frame drawn while it was
          // away, so a still frame must be drawn again.
          if (onScreen && !was) scheduler.invalidate();
          else scheduler.wake();
        },
        { rootMargin: `${ON_SCREEN_MARGIN_PX}px` },
      );
      io.observe(el);
    },
  };
}
